// ── "The system is being updated" — the pure half (Bugify deploy-now, owner 2026-11-03) ───────────────────────────
// The owner's ask: while a deploy runs, the people using the system must see that it is being updated, so nobody takes
// a restart for a dead system. deploy.sh writes build/deploy-status.json on the server it deploys (scripts/
// deploy-status.sh): {v:1, state:'deploying'|'restarting'|'idle', build:'main.<hex>.js', startedAt, expiresAt}, every
// time in ms by the SERVER's clock. Every page of every installation reads that file (services/apiHealth.ts) and draws
// the notice (components/DeployNotice.tsx).
// This file is the part that decides, kept free of React, the DOM, i18n and the API client, because the server's test
// suite (server/src/scripts/testBugifyReleaseNow.ts) requires it as it is.
//
// Two rules keep the notice honest:
//   • A busy file that has outlived itself is idle. A deploy that died between its first write and its last would
//     otherwise leave "updating" on every screen for ever — so expiresAt, and the file's own age, end it, judged by the
//     server's clock (the response's Date header) whenever there is one, since a laptop's clock can be hours off.
//   • An idle file is never stale. Its build names what the server serves now, which is how a page that has been open
//     since before a deploy learns that a newer version is waiting (isNewBuild).

export type DeployState = 'idle' | 'deploying' | 'restarting';
export interface DeployStatus { state: DeployState; build: string; startedAt: number; stale: boolean; }
export const DEPLOY_STATUS_MAX_AGE_MS = 15 * 60_000;

const STATES: DeployState[] = ['idle', 'deploying', 'restarting'];
// The shape deploy-status.sh reads from the live index.html (a real build's hash is hex, as utils/appUpdate.tsx reads
// the page's own script tag); anything else in the field is dropped, since the file is public.
const BUILD_NAME = /^main\.[0-9A-Za-z_-]{1,64}\.js$/;
const IDLE: DeployStatus = { state: 'idle', build: '', startedAt: 0, stale: false };

const num = (v: any): number => (typeof v === 'number' && isFinite(v) ? v : 0);
const httpDate = (v?: string | null): number => {
  const ms = v ? Date.parse(v) : NaN;
  return isFinite(ms) ? ms : NaN;
};

/** Missing/HTML/invalid JSON/v!==1/unknown state → {state:'idle', build:'', startedAt:0, stale:false}.
 *  serverNow = Date header (else clientNowMs ?? Date.now()). A NON-IDLE file is stale when serverNow > expiresAt or
 *  serverNow − Last-Modified > MAX_AGE (that check skipped without the header); a stale non-idle file is returned as
 *  {state:'idle', stale:true, build kept}. An idle file is never stale and always keeps its build. */
export function parseDeployStatus(text: string, headers: { date?: string | null; lastModified?: string | null }, clientNowMs?: number): DeployStatus {
  let o: any = null;
  try { o = JSON.parse(String(text || '')); } catch { return { ...IDLE }; }
  if (!o || typeof o !== 'object' || o.v !== 1 || !STATES.includes(o.state)) return { ...IDLE };
  const build = typeof o.build === 'string' && BUILD_NAME.test(o.build) ? o.build : '';
  const startedAt = num(o.startedAt);
  if (o.state === 'idle') return { state: 'idle', build, startedAt, stale: false };
  const date = httpDate(headers && headers.date);
  const serverNow = isFinite(date) ? date : (typeof clientNowMs === 'number' && isFinite(clientNowMs) ? clientNowMs : Date.now());
  // A busy file with no expiry of its own is treated as expired: nothing may keep the notice up for ever.
  const expiresAt = num(o.expiresAt);
  const modified = httpDate(headers && headers.lastModified);
  const stale = serverNow > expiresAt || (isFinite(modified) && serverNow - modified > DEPLOY_STATUS_MAX_AGE_MS);
  return stale ? { state: 'idle', build, startedAt, stale: true } : { state: o.state, build, startedAt, stale: false };
}

/** true when status.build and loaded are both non-empty, differ, and status.build !== reloadedFor. */
export function isNewBuild(status: DeployStatus, loaded: string, reloadedFor: string): boolean {
  const live = (status && status.build) || '';
  return !!live && !!loaded && live !== loaded && live !== reloadedFor;
}

/** true for a fetch failure (any error except name==='AbortError') or status 502/503/504 with payload == null. */
export function isApiDownSignal(e: any): boolean {
  if (!e) return false;
  if (e.name === 'AbortError') return false;
  if (typeof e.status === 'number') return (e.status === 502 || e.status === 503 || e.status === 504) && e.payload == null;
  return true;
}

/** How far a bar with nothing to measure should be: quick at first, then ever slower, and never full — a restart that
 *  takes three minutes instead of one must still look alive, never finished. */
export function estimatedPct(elapsedMs: number): number {
  const t = typeof elapsedMs === 'number' && isFinite(elapsedMs) ? Math.max(0, elapsedMs) : 0;
  return Math.min(95, Math.max(0, Math.round(95 * (1 - Math.exp(-t / 30000)))));
}

/** "m:ss" — minutes are not wrapped into hours (a 65-minute wait reads 65:00). */
export function fmtElapsed(ms: number): string {
  const s = Math.max(0, Math.floor((typeof ms === 'number' && isFinite(ms) ? ms : 0) / 1000));
  const sec = s % 60;
  return `${Math.floor(s / 60)}:${sec < 10 ? '0' : ''}${sec}`;
}

// ── Is the API back? ─────────────────────────────────────────────────────────────────────────────────────────────────
// One failed request starts probing GET /api/health; the first answer after ANY trouble is "back" (slf:api-back), which
// is what lets the sign-in check, the portal profile and everything else that gave up try again. Only the second failed
// probe in a row counts as down — a single blip must not throw a blocking card over somebody's work — unless the status
// file says the server is restarting, when the first failed probe is enough (overlayWanted below). An answer only counts
// if it was asked for after the trouble began (services/apiHealth.ts apiOutageMark): during a restart the old process
// still finishes the requests it already had, and those answers say nothing about the new one (review round 1).
export interface ProbeState { probing: boolean; fails: number; down: boolean; since: number; owedBack: boolean; }
export const PROBE_IDLE: ProbeState = { probing: false, fails: 0, down: false, since: 0, owedBack: false };

/** reportApiTrouble: start probing; owedBack = true; since = since || nowMs. */
export function probeTrouble(s: ProbeState, nowMs: number): ProbeState {
  return { ...s, probing: true, owedBack: true, since: s.since || nowMs };
}

/** One probe result. fail → fails+1, down = fails >= 2. ok → emitBack = s.owedBack, state = PROBE_IDLE, stop = true. */
export function probeStep(s: ProbeState, ok: boolean, nowMs: number): { state: ProbeState; emitBack: boolean; stop: boolean } {
  if (ok) return { state: { ...PROBE_IDLE }, emitBack: !!s.owedBack, stop: true };
  const fails = s.fails + 1;
  return { state: { ...s, probing: true, fails, down: s.down || fails >= 2, since: s.since || nowMs }, emitBack: false, stop: false };
}

// ── When, and what, the notice shows (live check 2026-11-04) ─────────────────────────────────────────────────────────
// On a real backend-only redeploy of test (deploy.sh be: "deploying" for 5 s, then "restarting" and a 19-s outage) an
// open Bugify page showed nothing for 16 s, then only the pill, with the "it restarts shortly" sentence after the restart
// had happened — and never the card. The rules below are what changed; services/apiHealth.ts and
// components/DeployNotice.tsx apply them, and server/src/scripts/testBugifyReleaseNow.ts tests them as they are.

/** The status file is read this often while the tab is in front and nothing is on (a hidden tab does not read it at all;
 *  it reads it at once when it comes back), and this often while a deploy is on. */
export const STATUS_IDLE_MS = 15_000, STATUS_BUSY_MS = 5000;
/** "Back online" stays at least this long, and the card is up at least OVERLAY_MIN_MS in all — never a flash. */
export const BACK_SHOW_MS = 1500, OVERLAY_MIN_MS = 2500;
/** pm2 stops the old process within ~2 s of the "restarting" write (the same ssh command): an answer from the API this
 *  long after that write comes from the new process, i.e. the restart is behind us. */
export const RESTART_GRACE_MS = 5000;

/** A busy status this tab has not re-read for this long is not trusted (review round 1). The file is read every 5 s while
 *  a deploy is on (STATUS_BUSY_MS) and after every failed probe — it is nginx's static file, so it stays readable
 *  through the API's restart — so a real deploy is re-confirmed several times inside this window. What it rules out is
 *  a "restarting" remembered from before a laptop slept or a phone locked: the read that would have cleared it either
 *  did not run (a hidden tab reads nothing) or could not reach the host, and the next network hiccup would have been
 *  taken for that deploy. */
export const STATUS_FRESH_MS = 30_000;

/** What the status file says NOW as far as this tab can tell: the last read's state while that read is recent
 *  (STATUS_FRESH_MS), else idle. */
export function freshState(status: DeployStatus | null | undefined, seenAtMs: number, nowMs: number): DeployState {
  if (!status || status.state === 'idle') return 'idle';
  return seenAtMs > 0 && isFinite(nowMs) && nowMs - seenAtMs < STATUS_FRESH_MS ? status.state : 'idle';
}

/** May the card block the page? (The caller adds: only for THIS installation's API, and not after "Continue anyway".)
 *   • the file says "restarting" right now (`restarting`, a fresh read): ONE failed /api/health probe is enough — the
 *     server is dropping its connections on purpose, and waiting for a second probe 3 s later is what left the live check
 *     without a card (its second probe already succeeded);
 *   • a deploy explains the outage otherwise (`explains`: the file says "deploying", or the outage began while a
 *     deploy was on — deployTailExplains): two failed probes in a row, as before. "deploying" covers minutes of building while the API keeps
 *     answering, so a one-second network blip in that time must not block the page (review round 1); the restart
 *     itself is written as "restarting" in the same ssh command as pm2 restart, and a failed request or probe re-reads
 *     the file at once, so the card loses nothing by waiting for that word;
 *   • no deploy at all: never — that is the non-blocking "Reconnecting…" banner, after two failed probes (a phone on a
 *     bad network must not have its page taken away on every blip). */
export function overlayWanted(p: ProbeState, restarting: boolean, explains: boolean): boolean {
  return (p.fails >= 1 && restarting) || (p.down && explains);
}

/** How long the card's "Back online" stays once the API answers: at least BACK_SHOW_MS, and long enough for the card to
 *  have been up OVERLAY_MIN_MS in all (a card that went up 0.3 s before the API came back still reads, then says so). */
export function backHoldMs(shownForMs: number): number {
  const shown = typeof shownForMs === 'number' && isFinite(shownForMs) ? Math.max(0, shownForMs) : OVERLAY_MIN_MS;
  return Math.max(BACK_SHOW_MS, OVERLAY_MIN_MS - shown);
}

/** Did the process that answered /api/health start BEFORE this restart was written? (`bootMs`: its `boot`, server clock;
 *  0 = an older server that does not say.) deploy.sh writes "restarting" in the same ssh command as pm2 restart, so the
 *  new process always starts after that write; one that started before it is the process being replaced. pm2 signals
 *  it and it stops listening at once — unless its event loop is blocked at that moment (a long start-up job; it has
 *  happened), when it can still accept and answer for a few seconds before it goes. Its 200 is not "back" (review
 *  round 2: it flashed "Back online", then the card again when the new process was not up yet). */
export function bootPredatesRestart(status: DeployStatus, bootMs: number): boolean {
  return !!status && status.state === 'restarting' && status.startedAt > 0 && typeof bootMs === 'number' && isFinite(bootMs)
    && bootMs > 0 && bootMs < status.startedAt;
}

/** The API answered while the file says "restarting": has the restart already happened? When the answer says which
 *  process gave it (`bootMs` > 0), that settles it: a process started at or after the write is the new one. Otherwise
 *  (an older server): yes when this tab saw the API fail meanwhile (`hadTrouble`), or when the answer came
 *  RESTART_GRACE_MS or more after the "restarting" write (server clock) — before that it may still be the old process,
 *  about to stop. */
export function restartLooksDone(status: DeployStatus, serverNowMs: number, hadTrouble: boolean, bootMs = 0): boolean {
  if (!status || status.state !== 'restarting') return false;
  if (typeof bootMs === 'number' && isFinite(bootMs) && bootMs > 0 && status.startedAt > 0) return bootMs >= status.startedAt;
  if (hadTrouble) return true;
  return status.startedAt > 0 && isFinite(serverNowMs) && serverNowMs - status.startedAt >= RESTART_GRACE_MS;
}

/** A "restarting" this tab has seen through — the API answered after it (restartBack) — and written this long ago, by the
 *  server's clock, is over, whatever the file still says. deploy.sh writes idle a few seconds after its own check that
 *  the API answers; when that write is lost (the ssh that ran pm2 restart drops after the restart, and set -e ends the
 *  script before it can write idle), the file says "restarting" until it expires 15 min later. Trusted for that long it
 *  kept every tab probing /api/health every 3 s, spinning "finishing the update…", and one failed probe away from the
 *  blocking card (review round 2). After this, the restart counts as idle; a real crash later is still explained by
 *  the deploy (two failed probes, deployExplains). */
export const RESTART_SETTLE_MS = 90_000;
export function restartSettled(status: DeployStatus, restartBack: boolean, serverNowMs: number): boolean {
  return !!status && status.state === 'restarting' && !!restartBack && status.startedAt > 0 && isFinite(serverNowMs)
    && serverNowMs - status.startedAt >= RESTART_SETTLE_MS;
}

/** May a deploy this tab saw explain an outage after the file is idle again, or while it cannot be read? It used to be
 *  "a busy read within the last 15 min" — so a client whose own Wi-Fi dropped ten minutes after a release got the
 *  blocking "Updating the system" card (review round 2). Now only an outage that BEGAN while the deploy was on:
 *   • a busy read within BUSY_FRESH_MS of now (the card still steps down after that, as before),
 *   • the outage began no more than BUSY_LINK_MS after that read (the file is read every 5 s while a deploy is on, so a
 *     deploy's outage always begins within seconds of one — or before it, when the read that saw it came after),
 *   • and no idle read came between that busy read and the outage's start (an idle read DURING the outage does not
 *     count: deploy.sh writes idle once the API answers it, which can be before it answers this tab).
 *  `idleSeenAt` is the FIRST idle read after that busy read (0 = none); `outageAtMs` is when the outage began (0 = none:
 *  judged as if it began now). All times are this tab's clock. */
export const BUSY_FRESH_MS = 15 * 60_000, BUSY_LINK_MS = 60_000;
export function deployTailExplains(busySeenAt: number, idleSeenAt: number, outageAtMs: number, nowMs: number): boolean {
  if (!(busySeenAt > 0) || !isFinite(nowMs) || nowMs - busySeenAt >= BUSY_FRESH_MS) return false;
  const at = outageAtMs > 0 ? outageAtMs : nowMs;
  if (at - busySeenAt >= BUSY_LINK_MS) return false;
  return !(idleSeenAt > busySeenAt && idleSeenAt <= at);
}

export type NoticeLine = '' | 'deploying' | 'restarting' | 'finishing';
/** Which sentence the pill says while the file is busy, so that it matches what is happening:
 *   deploying  → "being updated, unavailable shortly — save your work" (the restart is still ahead);
 *   restarting → "restarting to finish an update, back in a few seconds";
 *   finishing  → "back — finishing the update" (restarting, but the API already answers again);
 *   ''         → nothing, once this tab's card has said "Back online" for this restart (`backSaid`): a spinning
 *                "finishing" right after "Back online — you can carry on" read as the update not being done after all
 *                (review round 1). deploy.sh writes idle a few seconds later, after its own health check. */
export function noticeLine(state: DeployState, restartBack: boolean, backSaid = false): NoticeLine {
  if (state === 'deploying') return 'deploying';
  if (state === 'restarting') return restartBack ? (backSaid ? '' : 'finishing') : 'restarting';
  return '';
}

/** Where the pill's "Elapsed" counter starts (server ms), 0 = no counter. A deploy counts from its first write (busySince:
 *  "deploying" then "restarting" are one deploy); a restart from its own write, since the minutes of building before it
 *  are not the restart's and "restarting — back in a few seconds, Elapsed 1:52" would read as a restart gone wrong; the
 *  short "finishing" tail shows none. */
export function noticeSince(line: NoticeLine, busySince: number, statusStartedAt: number): number {
  if (line === 'deploying') return busySince || statusStartedAt || 0;
  if (line === 'restarting') return statusStartedAt || busySince || 0;
  return 0;
}
